import { Check, Copy, Loader2 } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useParams } from 'react-router-dom';

import { adminClient, type AdminOrganizationRow } from '@/api/adminClient';
import { OrgStudioActions } from '@/components/org/OrgStudioActions';
import { StatusPill } from '@/components/runs/StatusPill';
import { PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminPageLoadError } from '@/components/shell/AdminPageLoadError';
import { orgLabel, orgSecondaryLabel } from '@/lib/format';
import { orgBasePath } from '@/lib/orgNav';

type OrgScope = {
  orgId: string;
  org: AdminOrganizationRow;
  reloadOrg: () => Promise<void>;
};

const OrgScopeContext = createContext<OrgScope | undefined>(undefined);

/** The organization every page under `/organizations/:orgId` is about, loaded once by the layout. */
export function useOrgScope(): OrgScope {
  const scope = useContext(OrgScopeContext);
  if (!scope) throw new Error('useOrgScope must be used under OrgLayout');
  return scope;
}

/** An unknown path under an organization lands on its overview. */
export function OrgSectionNotFound() {
  const { orgId } = useOrgScope();
  return <Navigate to={orgBasePath(orgId)} replace />;
}

function CopyIdButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => setCopied(true));
      }}
      className="inline-flex h-8 max-w-[18rem] items-center gap-2 rounded-lg border border-border bg-card px-2.5 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
      aria-label="Copy organization id"
      title="Copy organization id"
    >
      <span className="truncate">{value}</span>
      {copied ? <Check className="h-3.5 w-3.5 shrink-0 text-success" /> : <Copy className="h-3.5 w-3.5 shrink-0" />}
    </button>
  );
}

function OrgTopBar({ org, onChanged }: { org: AdminOrganizationRow; onChanged: () => void }) {
  const label = orgLabel(org);
  const secondary = orgSecondaryLabel(org);
  const plan = org.billing?.productName;
  return (
    <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center justify-between gap-3 border-b border-border bg-card px-6">
      <div className="flex min-w-0 items-center gap-3">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-sm font-semibold text-foreground"
          aria-hidden="true"
        >
          {label.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <h1 className="truncate text-sm font-semibold text-foreground">{label}</h1>
          {secondary ? <p className="truncate text-xs text-muted-foreground">{secondary}</p> : null}
        </div>
        <div className="hidden shrink-0 items-center gap-2 md:flex">
          <StatusPill tone={org.studioAccess ? 'success' : 'neutral'}>
            {org.studioAccess ? 'Studio access' : 'No Studio access'}
          </StatusPill>
          {plan ? <StatusPill tone="neutral">{plan}</StatusPill> : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <OrgStudioActions orgId={org.organizationId} name={label} onChanged={onChanged} />
        <div className="hidden sm:block">
          <CopyIdButton value={org.organizationId} />
        </div>
      </div>
    </header>
  );
}

export function OrgLayout() {
  const { orgId = '' } = useParams();
  const [org, setOrg] = useState<AdminOrganizationRow | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  const reloadOrg = useCallback(async () => {
    setOrg(await adminClient.getOrganization(orgId));
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    adminClient
      .getOrganization(orgId)
      .then((row) => {
        if (!cancelled) setOrg(row);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load organization');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, reloadKey]);

  if (loading && org?.organizationId !== orgId) {
    return (
      <div className={PAGE_CONTAINER_CLASS}>
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading organization
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <AdminPageLoadError
        message={error}
        onRetry={() => setReloadKey((key) => key + 1)}
        backTo="/organizations"
        backLabel="Back to organizations"
      />
    );
  }

  if (!org) {
    return (
      <div className={PAGE_CONTAINER_CLASS}>
        <div className="space-y-3">
          <p className="text-sm text-destructive">Organization not found</p>
          <Link to="/organizations" className="text-sm text-primary hover:underline">
            Back to organizations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <OrgScopeContext.Provider value={{ orgId, org, reloadOrg }}>
      <div className="flex min-h-full flex-col">
        <OrgTopBar org={org} onChanged={() => void reloadOrg()} />
        <div className="min-h-0 flex-1">
          <Outlet />
        </div>
      </div>
    </OrgScopeContext.Provider>
  );
}
